import { CalendarRange, ChevronLeft, ChevronRight, Plus, Repeat, Square, UserRound } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { EmptyState } from "../components/EmptyState";
import { PrintButton, PrintSheetHeading } from "../components/PrintSheet";
import { formatMinutes } from "../../shared/duration";
import { dateKeyInZone, normalizeTimeZone } from "../../shared/time-zone";
import { addDays, formatDayMinute, intervalWeeksLabel, MAX_INTERVAL_WEEKS, parseDayMinute, weekdayOf, weekStartOf, WEEKDAY_LABELS } from "../../shared/week";
import type { RouterOutputs, UserSession } from "../lib/trpc";
import { trpc } from "../lib/trpc";

type Series = RouterOutputs["scheduling"]["series"][number];

function giornoLeggibile(dateKey: string) {
  const [anno, mese, giorno] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(anno, mese - 1, giorno)).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function periodoLeggibile(weekStart: string) {
  return `${giornoLeggibile(weekStart)} - ${giornoLeggibile(addDays(weekStart, 6))}`;
}

/** Ogni quanto torna il giro, detto come lo direbbe una persona. */
export function ritmoLeggibile(series: Pick<Series, "weekdayLabel" | "startMinute" | "intervalWeeks">) {
  const quando = `${series.weekdayLabel} alle ${formatDayMinute(series.startMinute)}`;
  return `${quando}, ${intervalWeeksLabel(series.intervalWeeks)}`;
}

/** Le cadenze offerte dal modulo: da ogni settimana a ogni otto, scritte a parole. */
const CADENZE = Array.from({ length: MAX_INTERVAL_WEEKS }, (_, indice) => indice + 1);

export function WeekPage({ session }: { session: UserSession }) {
  const canManage = session.membership.role !== "collaborator";
  const timeZone = normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const utils = trpc.useUtils();
  const [weekStart, setWeekStart] = useState(() => weekStartOf(dateKeyInZone(new Date(), timeZone)));
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Series | null>(null);

  const week = trpc.scheduling.week.useQuery({ weekStart, timeZone });
  const series = trpc.scheduling.series.useQuery();
  const sites = trpc.sites.list.useQuery(undefined, { enabled: canManage });
  const team = trpc.team.list.useQuery(undefined, { enabled: canManage });
  const catalog = trpc.catalog.list.useQuery(undefined, { enabled: canManage });
  const ensureWeek = trpc.scheduling.ensureWeek.useMutation({
    onSuccess: (esito) => {
      if (esito && esito.create > 0) void utils.scheduling.week.invalidate();
    },
  });
  const createSeries = trpc.scheduling.createSeries.useMutation({ onSuccess: () => void utils.scheduling.invalidate() });
  const updateSeries = trpc.scheduling.updateSeries.useMutation({ onSuccess: () => void utils.scheduling.invalidate() });
  const stopSeries = trpc.scheduling.stopSeries.useMutation({ onSuccess: () => void utils.scheduling.invalidate() });
  const salvataggio = editing ? updateSeries : createSeries;

  // Gli interventi delle settimane oltre l'orizzonte automatico si generano guardandole.
  // La chiamata e ripetibile senza danni, quindi non serve ricordarsi quali settimane
  // sono gia state generate: se non c'e niente da fare, non fa niente.
  useEffect(() => {
    if (!canManage) return;
    ensureWeek.mutate({ weekStart });
    // Volutamente non fra le dipendenze: la mutazione cambia identita a ogni ridisegno e
    // rimetterla qui farebbe ripartire l'effetto all'infinito. Conta solo la settimana guardata.
  }, [weekStart, canManage]);

  const giorni = week.data?.days ?? [];
  const oggi = dateKeyInZone(new Date(), timeZone);
  const giriAttivi = useMemo(() => (series.data ?? []).filter((giro) => giro.active), [series.data]);
  const giriFermati = useMemo(() => (series.data ?? []).filter((giro) => !giro.active), [series.data]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const jobTypeIds = data.getAll("jobTypeIds").map(String);
    const lavori = catalog.data?.flatMap((categoria) => categoria.jobTypes) ?? [];
    const attivita: Array<{ jobTypeId: string | null; label: string }> = jobTypeIds
      .map((id) => lavori.find((lavoro) => lavoro.id === id))
      .filter((lavoro): lavoro is NonNullable<typeof lavoro> => Boolean(lavoro))
      .map((lavoro) => ({ jobTypeId: lavoro.id, label: lavoro.name }));
    const altra = String(data.get("customWork") || "").trim();
    if (altra.length >= 2) attivita.push({ jobTypeId: null, label: altra });
    const durata = String(data.get("durationMinutes") || "").trim();
    const fine = String(data.get("endDate") || "").trim();
    const input = {
      siteId: String(data.get("siteId")),
      title: String(data.get("title")),
      description: String(data.get("description") || "") || null,
      weekday: Number(data.get("weekday")),
      startMinute: parseDayMinute(String(data.get("startTime"))) ?? 8 * 60,
      durationMinutes: durata ? Number(durata) : null,
      priority: Number(data.get("priority")),
      intervalWeeks: Number(data.get("intervalWeeks")),
      startDate: String(data.get("startDate")),
      endDate: fine || null,
      timeZone,
      assigneeIds: data.getAll("assigneeIds").map(String),
      activities: attivita,
      confirmSiteAssignments: true,
    };
    try {
      if (editing) await updateSeries.mutateAsync({ ...input, id: editing.id });
      else await createSeries.mutateAsync(input);
      setOpenForm(false);
      setEditing(null);
    } catch {
      // L'errore resta nella mutazione e la scheda lo mostra sotto al modulo.
    }
  }

  function apriNuovo() {
    setEditing(null);
    setOpenForm(true);
  }

  function apriModifica(giro: Series) {
    setEditing(giro);
    setOpenForm(true);
  }

  const jobTypeSelezionati = new Set((editing?.activities ?? []).map((attivita) => attivita.jobTypeId ?? ""));
  const attivitaLibera = (editing?.activities ?? []).find((attivita) => !attivita.jobTypeId)?.label ?? "";

  return (
    <main className="page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Programmazione settimanale</p>
          <h1>Settimana</h1>
          <p>Il programma per collaboratore, giorno per giorno. I giri fissi si ripetono da soli.</p>
        </div>
        <div className="page-heading__actions">
          <div className="week-nav" role="group" aria-label="Cambia settimana">
            <button type="button" className="icon-button" aria-label="Settimana precedente" onClick={() => setWeekStart(addDays(weekStart, -7))}>
              <ChevronLeft size={20} />
            </button>
            <button type="button" className="button button--ghost" onClick={() => setWeekStart(weekStartOf(oggi))}>Questa settimana</button>
            <button type="button" className="icon-button" aria-label="Settimana successiva" onClick={() => setWeekStart(addDays(weekStart, 7))}>
              <ChevronRight size={20} />
            </button>
          </div>
          <PrintButton />
          {canManage && <button className="button button--primary" type="button" onClick={apriNuovo}><Plus size={19} /> Nuovo giro fisso</button>}
        </div>
      </header>

      <p className="week-period"><CalendarRange size={18} aria-hidden="true" /> {periodoLeggibile(weekStart)}</p>

      {/* Il foglio della settimana: sulla carta deve esserci scritto di quale settimana si parla. */}
      <PrintSheetHeading title="Programma della settimana" details={[periodoLeggibile(weekStart)]} />

      {openForm && canManage && (
        <form className="editor-card" onSubmit={submit}>
          <div className="editor-card__heading">
            <h2>{editing ? "Modifica il giro fisso" : "Nuovo giro fisso"}</h2>
            <p>Un programma che torna da ogni settimana fino a ogni otto. Gli interventi futuri nascono da qui.</p>
          </div>
          <div className="form-grid">
            <label><span>Titolo</span><input name="title" required minLength={2} defaultValue={editing?.title ?? ""} placeholder="Giro piscine" /></label>
            <label><span>Struttura</span><select name="siteId" required defaultValue={editing?.siteId ?? ""}>{sites.data?.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>
            <label><span>Giorno</span><select name="weekday" defaultValue={String(editing?.weekday ?? 2)}>{Object.entries(WEEKDAY_LABELS).map(([numero, nome]) => <option key={numero} value={numero}>{nome}</option>)}</select></label>
            <label><span>Ora di inizio</span><input name="startTime" type="time" required defaultValue={formatDayMinute(editing?.startMinute ?? 8 * 60)} /></label>
            <label><span>Durata in minuti</span><input name="durationMinutes" type="number" min={15} max={1440} step={15} defaultValue={editing?.durationMinutes ?? 90} /></label>
            <label>
              <span>Ogni quanto</span>
              <select name="intervalWeeks" defaultValue={String(editing?.intervalWeeks ?? 1)}>
                {CADENZE.map((settimane) => (
                  <option key={settimane} value={settimane}>
                    {intervalWeeksLabel(settimane).replace("ogni", "Ogni")}
                  </option>
                ))}
              </select>
            </label>
            <label><span>Dal giorno</span><input name="startDate" type="date" required defaultValue={editing?.startDate ?? oggi} /></label>
            <label><span>Fino al giorno</span><input name="endDate" type="date" defaultValue={editing?.endDate ?? ""} /></label>
            <label><span>Priorita</span><select name="priority" defaultValue={String(editing?.priority ?? 2)}><option value="1">Alta</option><option value="2">Normale</option><option value="3">Bassa</option></select></label>
            <fieldset className="form-grid__wide assignee-field">
              <legend>Collaboratori</legend>
              <div>
                {team.data?.filter((membro) => membro.active).map((membro) => (
                  <label className="check-control" key={membro.userId}>
                    <input
                      type="checkbox"
                      name="assigneeIds"
                      value={membro.userId}
                      defaultChecked={editing ? editing.assignees.some((persona) => persona.userId === membro.userId) : membro.role === "collaborator"}
                    /> {membro.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset className="form-grid__wide assignee-field">
              <legend>Attivita del giro</legend>
              <div>
                {catalog.data?.flatMap((categoria) => categoria.jobTypes.filter((lavoro) => lavoro.active).map((lavoro) => (
                  <label className="check-control" key={lavoro.id}>
                    <input type="checkbox" name="jobTypeIds" value={lavoro.id} defaultChecked={jobTypeSelezionati.has(lavoro.id)} /> {categoria.name}: {lavoro.name}
                  </label>
                )))}
              </div>
            </fieldset>
            <label className="form-grid__wide"><span>Altra attivita</span><input name="customWork" defaultValue={attivitaLibera} placeholder="Scrivila se non e in catalogo" /></label>
            <label className="form-grid__wide"><span>Note</span><textarea name="description" rows={2} defaultValue={editing?.description ?? ""} placeholder="Accessi, attenzioni particolari" /></label>
          </div>
          {salvataggio.error && <p className="form-error">{salvataggio.error.message}</p>}
          {editing && <p className="form-hint">Le occorrenze future non ancora toccate vengono rifatte con il programma nuovo. Il lavoro di oggi, quello gia iniziato e quello gia modificato restano come sono.</p>}
          <div className="form-actions">
            <button className="button button--ghost" type="button" onClick={() => { setOpenForm(false); setEditing(null); }}>Annulla</button>
            <button className={`button button--primary${salvataggio.isPending ? " button--saving" : ""}`}>{salvataggio.isPending ? "Salvataggio..." : editing ? "Salva il giro" : "Crea il giro"}</button>
          </div>
        </form>
      )}

      <section className="section-block">
        <div className="section-heading">
          <div><p className="eyebrow">Programma</p><h2>La settimana della squadra</h2></div>
        </div>
        {week.isLoading && <p className="loading-copy">Caricamento della settimana...</p>}
        {!week.isLoading && week.isError && (
          <EmptyState title="Impossibile caricare la settimana">
            <button type="button" className="button button--outline" onClick={() => void week.refetch()}>Riprova</button>
          </EmptyState>
        )}
        {!week.isLoading && !week.isError && week.data && week.data.rows.length === 0 && (
          <EmptyState title="Nessuno in squadra">Aggiungi i collaboratori per vedere il loro programma.</EmptyState>
        )}
        {!week.isLoading && !week.isError && week.data && week.data.rows.length > 0 && (
          <div className="week-board-scroll">
            <table className="week-board">
              <caption className="visually-hidden">Programma della settimana dal {periodoLeggibile(weekStart)}</caption>
              <thead>
                <tr>
                  <th scope="col">Collaboratore</th>
                  {giorni.map((giorno) => (
                    <th scope="col" key={giorno} className={giorno === oggi ? "week-board__today" : undefined}>
                      <span>{WEEKDAY_LABELS[weekdayOf(giorno)]}</span>
                      <small>{giornoLeggibile(giorno)}</small>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {week.data.rows.map((riga) => (
                  <tr key={riga.userId ?? "senza-collaboratore"}>
                    <th scope="row"><UserRound size={17} aria-hidden="true" /> {riga.name}</th>
                    {riga.days.map((blocchi, indice) => (
                      <td key={giorni[indice] ?? indice} className={giorni[indice] === oggi ? "week-board__today" : undefined}>
                        {blocchi.length === 0 && <span className="week-board__empty" aria-hidden="true">-</span>}
                        {blocchi.map((blocco) => (
                          <Link className={`week-block week-block--${blocco.status}`} key={blocco.interventionId} to={`/interventi/${blocco.interventionId}`}>
                            <strong>{blocco.siteName}</strong>
                            <span>{formatDayMinute(blocco.startMinute)}{blocco.durationMinutes ? ` - ${formatMinutes(blocco.durationMinutes)}` : ""}</span>
                            <small>{blocco.title}{blocco.seriesId ? " " : ""}{blocco.seriesId && <Repeat size={13} aria-label="Da un giro fisso" />}</small>
                          </Link>
                        ))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="section-block">
        <div className="section-heading">
          <div><p className="eyebrow">Ricorrenze</p><h2>I giri fissi</h2></div>
        </div>
        {series.isLoading && <p className="loading-copy">Caricamento dei giri...</p>}
        {!series.isLoading && giriAttivi.length === 0 && (
          <EmptyState title="Nessun giro fisso">
            {canManage ? "Crea il primo programma che si ripete: il lavoro di ogni settimana nasce da solo." : "Il titolare non ha ancora impostato giri fissi."}
          </EmptyState>
        )}
        {giriAttivi.length > 0 && (
          <ul className="series-list">
            {giriAttivi.map((giro) => (
              <li className="series-card" key={giro.id}>
                <div className="series-card__main">
                  <strong>{giro.siteName}</strong>
                  <span>{giro.title}</span>
                  <small><Repeat size={14} aria-hidden="true" /> {ritmoLeggibile(giro)}</small>
                </div>
                <div className="series-card__people">
                  {giro.assignees.map((persona) => persona.name).join(", ") || "Nessun collaboratore"}
                  {giro.nextOccurrence && <small>Prossima volta: {giornoLeggibile(giro.nextOccurrence)}</small>}
                </div>
                {canManage && (
                  <div className="series-card__actions">
                    <button type="button" className="button button--outline" onClick={() => apriModifica(giro)}>Modifica</button>
                    <button type="button" className="button button--ghost" onClick={() => stopSeries.mutate({ id: giro.id })}><Square size={16} /> Ferma</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {stopSeries.error && <p className="form-error">{stopSeries.error.message}</p>}
        {giriFermati.length > 0 && (
          <p className="series-stopped">Giri fermati: {giriFermati.map((giro) => giro.title).join(", ")}.</p>
        )}
      </section>
    </main>
  );
}
